import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, tap, of, catchError, map } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { User, TokenPair, LoginRequest, TelegramAuthResponse } from '../models/auth.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly TOKEN_KEY = 'booking_access_token';
  private readonly REFRESH_KEY = 'booking_refresh_token';
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  private isBrowser: boolean;

  public currentUser$ = this.currentUserSubject.asObservable();
  public isAuthenticated$ = this.currentUser$.pipe(
    map(user => !!user)
  );

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  get currentUser(): User | null {
    return this.currentUserSubject.value;
  }

  get accessToken(): string | null {
    if (!this.isBrowser) return null;
    return localStorage.getItem(this.TOKEN_KEY);
  }

  get refreshToken(): string | null {
    if (!this.isBrowser) return null;
    return localStorage.getItem(this.REFRESH_KEY);
  }

  login(credentials: LoginRequest): Observable<TokenPair> {
    return this.http.post<TokenPair>(`${environment.apiUrl}/api/token/`, credentials).pipe(
      tap(tokens => {
        this.setTokens(tokens);
        this.fetchCurrentUser().subscribe();
      }),
    );
  }

  loginTelegram(initData: object): Observable<TelegramAuthResponse> {
    return this.http.post<TelegramAuthResponse>(`${environment.apiUrl}/tg/auth/`, initData).pipe(
      tap(response => {
        this.setTokens({ access: response.access, refresh: response.refresh });
        this.fetchCurrentUser().subscribe();
      }),
    );
  }

  refreshAccessToken(): Observable<TokenPair> {
    const refresh = this.refreshToken;
    if (!refresh) {
      return new Observable(subscriber => {
        subscriber.error('No refresh token');
        subscriber.complete();
      });
    }
    return this.http.post<TokenPair>(`${environment.apiUrl}/api/token/refresh/`, { refresh }).pipe(
      tap(tokens => this.setTokens(tokens)),
    );
  }

  fetchCurrentUser(): Observable<User> {
    return this.http.get<User>(`${environment.apiUrl}/api/user/me/`).pipe(
      tap(user => this.currentUserSubject.next(user)),
    );
  }

  logout(): void {
    this.clearTokens();
    this.currentUserSubject.next(null);
  }

  initializeAuth(): Observable<User | null> {
    if (this.accessToken) {
      return this.fetchCurrentUser().pipe(
        catchError(() => {
          this.clearTokens();
          this.currentUserSubject.next(null);
          return of(null);
        }),
      );
    }
    this.currentUserSubject.next(null);
    return of(null);
  }

  private setTokens(tokens: TokenPair): void {
    if (!this.isBrowser) return;
    localStorage.setItem(this.TOKEN_KEY, tokens.access);
    localStorage.setItem(this.REFRESH_KEY, tokens.refresh);
  }

  private clearTokens(): void {
    if (!this.isBrowser) return;
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_KEY);
  }
}